import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import CAMPAIGN from "@/data/campaign";

const EASE = [0.76, 0, 0.24, 1];

const Reviews = () => {
  const r = CAMPAIGN.reviews;

  return (
    <section id="reviews" className="py-24 md:py-40 bg-[#EFEADF] border-t border-border" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-8 md:gap-16 mb-16 md:mb-24 items-end">
          <div className="md:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: EASE }}
              className="uppercase text-xs tracking-[0.25em] font-semibold text-primary mb-6"
              data-testid="reviews-overline"
            >
              {r.overline}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
              className="font-heading font-light tracking-tight text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.02]"
              data-testid="reviews-heading"
            >
              {r.headingPlain}<br /><em className="text-primary">{r.headingAccent}</em>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
            className="md:col-span-5 text-base md:text-lg font-light text-foreground/75 leading-relaxed"
            data-testid="reviews-intro"
          >
            {r.intro}
          </motion.p>
        </div>

        <div className="grid sm:grid-cols-2 gap-px bg-border border border-border" data-testid="reviews-grid">
          {r.items.map((rev, i) => (
            <motion.figure
              key={rev.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.85, ease: EASE, delay: (i % 2) * 0.12 }}
              className="bg-[#EFEADF] p-8 md:p-12 flex flex-col justify-between group hover:bg-secondary/50 transition-colors duration-500"
              data-testid={`review-card-${i}`}
            >
              <div>
                <div className="flex items-center justify-between mb-8">
                  <div className="flex gap-1" aria-label="5 out of 5 stars">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} className="w-4 h-4 fill-primary text-primary" />
                    ))}
                  </div>
                  <Quote className="w-8 h-8 text-primary/25 transition-transform duration-500 group-hover:-translate-y-1" />
                </div>
                <blockquote className="font-heading italic font-light text-2xl md:text-[1.7rem] leading-snug text-foreground">
                  "{rev.quote}"
                </blockquote>
              </div>
              <figcaption className="mt-10 pt-6 border-t border-border">
                <p className="text-sm font-semibold text-foreground">{rev.name}</p>
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mt-1">{rev.meta}</p>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Reviews;
